import React, { createContext, useContext, useState } from "react";
import {
  generateQuestionPrompt,
  regenerateQuestionPrompt,
  generateQuestionByExamplePrompt,
  regenerateQuestionByExamplePrompt,
} from "../prompts";
import { useClient } from "./ClientContext";
import { extractQuestionAndExplanation } from "../utils/questionParsing";

// Create a Context
const QuestionsContext = createContext();

export const QuestionsProvider = ({ children }) => {
  const { client } = useClient();
  const defaultModel = "claude-sonnet-4-20250514";

  const [tokenUsage, setTokenUsage] = useState(() => ({
    input: Number(sessionStorage.getItem("totalInputTokens") || 0),
    output: Number(sessionStorage.getItem("totalOutputTokens") || 0),
  }));

  /**
   * Generates a new question using the Claude AI API based on form input.
   * @param {Object} formData - Form inputs used to build the prompt.
   * @param {Function} updateFields - Callback to update form fields with results or errors.
   */
  const handleQuestionGeneration = async (formData, updateFields) => {
    // Indicate loading state in the UI
    updateFields({
      question: "טוען...",
      solution: "טוען...",
      quotation: "",
    });

    // Choose prompt format depending on whether to include an example
    const prompt = formData.showExampleInput
      ? generateQuestionByExamplePrompt(formData) // Use example-based prompt
      : generateQuestionPrompt(formData); // Use standard prompt

    // Ensure the Claude client is properly initialized
    if (!client || typeof client.messages?.create !== "function") {
      console.error(
        " Claude client is not initialized – possible missing API key."
      );

      updateFields({
        question: "Claude לא מוגדר – ייתכן שהמפתח חסר או לא הוזן.",
        solution: "אנא הזן מפתח API תקף או התחבר מחדש.",
      });
      return;
    }
    try {
      // Send request to Claude with selected model and generated prompt
      const model = sessionStorage.getItem("anthropicModel") || defaultModel;
      const message = await client.messages.create({
        model,
        max_tokens: 2048,
        messages: [{ role: "user", content: prompt }],
      });

      // Track and update token usage statistics if available
      if (message?.usage) {
        const prevInput = Number(
          sessionStorage.getItem("totalInputTokens") || 0
        );
        const prevOutput = Number(
          sessionStorage.getItem("totalOutputTokens") || 0
        );

        const newInput = prevInput + message.usage.input_tokens;
        const newOutput = prevOutput + message.usage.output_tokens;

        sessionStorage.setItem("totalInputTokens", newInput.toString());
        sessionStorage.setItem("totalOutputTokens", newOutput.toString());

        updateSessionCost(
          message.usage.input_tokens,
          message.usage.output_tokens
        );
        setTokenUsage({ input: newInput, output: newOutput });
      }

      // Parse and populate the generated question and explanation
      extractQuestionAndExplanation(
        formData.questionType,
        message.content[0].text,
        updateFields
      );
    } catch (err) {
      console.err(err);

      // Handle HTTP errors with specific messages
      if (err.response) {
        const status = err.response.status;

        const errorMap = {
          401: {
            question: "🔐 מפתח API שגוי או חסר.",
            solution: "בדוק את המפתח שהזנת ונסה שוב.",
          },
          403: {
            question: "🚫 אין לך הרשאה לשימוש ב-API.",
            solution: "פנה לתמיכה או ודא שלמפתח יש גישה.",
          },
          429: {
            question: "⏳ חרגת ממכסת השימוש שלך.",
            solution: "נסה שוב מאוחר יותר.",
          },
          500: {
            question: "⚠️ שגיאה פנימית בשרת של Claude.",
            solution: "נסה שוב בעוד כמה רגעים.",
          },
        };

        updateFields(
          errorMap[status] || {
            question: `❌ שגיאה לא צפויה (קוד ${status})`,
            solution: "נסה שוב או פנה לתמיכה.",
          }
        );
      }
      // Handle case where no response was received from the server
      else if (err.request) {
        updateFields({
          question: "🌐 לא התקבלה תגובה משרת Claude.",
          solution: "בדוק את חיבור האינטרנט שלך.",
        });
      }
      // Handle unexpected or general client-side errors
      else {
        updateFields({
          question: "❌ שגיאה כללית בעת יצירת השאלה.",
          solution: "נסה שוב.",
        });
      }
    }
  };

  /**
   * Regenerates a question based on previous inputs and user feedback,
   * by sending an updated prompt to the Claude AI API.
   *
   * @param {Object} formData - Original form inputs
   * @param {string} improvementFeedback - User feedback to improve the question
   * @param {Object} selectedToggles - Toggle settings affecting prompt generation
   * @param {Function} updateFields - Callback to update UI with new results or errors
   */
  const handleRegenerate = async (
    formData,
    improvementFeedback,
    selectedToggles,
    updateFields
  ) => {
    // Show loading state in the UI
    updateFields({
      question: "טוען...",
      solution: "טוען...",
      quotation: "",
    });

    // Build the prompt based on whether example input is enabled
    const promptFeedback = formData.showExampleInput
      ? regenerateQuestionByExamplePrompt(
          formData,
          improvementFeedback,
          selectedToggles
        )
      : regenerateQuestionPrompt(
          formData,
          improvementFeedback,
          selectedToggles
        );

    try {
      // Send prompt to Claude for response
      const model = sessionStorage.getItem("anthropicModel") || defaultModel;
      const message = await client.messages.create({
        model,
        max_tokens: 2048,
        messages: [{ role: "user", content: promptFeedback }],
      });

      // Handle case where no usable response was returned
      if (!message?.content?.[0]?.text) {
        console.error("❌ Claude returned no content.");
        updateFields({
          question: "לא התקבלה שאלה מהמודל.",
          solution: "נסה שנית או ודא שהחומר מתאים.",
        });
        return;
      }

      // Update usage statistics if available
      if (message?.usage) {
        const prevInput = Number(
          sessionStorage.getItem("totalInputTokens") || 0
        );
        const prevOutput = Number(
          sessionStorage.getItem("totalOutputTokens") || 0
        );

        const newInput = prevInput + message.usage.input_tokens;
        const newOutput = prevOutput + message.usage.output_tokens;

        sessionStorage.setItem("totalInputTokens", newInput.toString());
        sessionStorage.setItem("totalOutputTokens", newOutput.toString());

        updateSessionCost(
          message.usage.input_tokens,
          message.usage.output_tokens
        );
        setTokenUsage({ input: newInput, output: newOutput });
      }

      // Extract and display the regenerated question and solution
      const rawText = message.content[0].text;
      extractQuestionAndExplanation(
        formData.questionType,
        rawText,
        updateFields
      );
    } catch (err) {
      console.error("❌ Error during regeneration:", err);
      // Can show fallback error UI via updateFields()
    } finally {
      // setLoading(false);
    }
  };

  /**
   * Retrieves the current session's token usage and estimated cost from sessionStorage.
   *
   * @returns {Object} An object containing:
   *   - inputTokens {number}: Total input tokens used
   *   - outputTokens {number}: Total output tokens generated
   *   - dollars {number}: Estimated cost in USD
   */
  const getSessionCost = () => {
    return {
      inputTokens: Number(sessionStorage.getItem("totalInputTokens") || 0),
      outputTokens: Number(sessionStorage.getItem("totalOutputTokens") || 0),
      dollars: parseFloat(sessionStorage.getItem("sessionCost") || "0"),
    };
  };

  /**
   * Updates the session's total cost in USD based on token usage and model rates.
   *
   * @param {number} promptTokens - Number of tokens used for the prompt (input).
   * @param {number} completionTokens - Number of tokens generated in the response (output).
   */
  const updateSessionCost = (promptTokens, completionTokens) => {
    const model = sessionStorage.getItem("anthropicModel") || defaultModel;

    // Define per-token rates based on the model type
    let inputRate, outputRate;
    if (model.includes("haiku")) {
      // Claude Haiku pricing
      inputRate = 0.8 / 1e6;     // $0.80 per 1M input tokens
      outputRate = 4 / 1e6;      // $4.00 per 1M output tokens
    } else {
      // Claude Sonnet / Opus pricing
      inputRate = 3 / 1e6;       // $3.00 per 1M input tokens
      outputRate = 15 / 1e6;     // $15.00 per 1M output tokens
    }

    // Calculate current call cost
    const cost = promptTokens * inputRate + completionTokens * outputRate;

    // Retrieve and update total session cost
    const previous = parseFloat(sessionStorage.getItem("sessionCost") || "0");
    const newTotal = previous + cost;

    sessionStorage.setItem("sessionCost", newTotal.toFixed(6)); // Save with 6 decimal places
  };

  return (
    //<QuestionsContext.Provider value={{ questions, setQustions, handleQuestionGeneration, handleRegenerate }}>
    <QuestionsContext.Provider
      value={{ handleQuestionGeneration, handleRegenerate, getSessionCost }}
    >
      {children}
    </QuestionsContext.Provider>
  );
};

export const useQuestions = () => {
  return useContext(QuestionsContext);
};
